'use client'

import React, { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import { DynamicWidgetRenderer } from './DynamicWidgetRenderer'
import { ClarificationForm } from './ClarificationForm'
import type { ChatMessage as ChatMessageType, UIComponentData } from '@/context/AIContext'

interface IntegrationBadge {
  name: string
  id: string
  system_type: string
}

interface ToolbeltSuggestion {
  command?: string
  should_add: boolean
}

interface MessageMetadata {
  integration_badge?: IntegrationBadge
  toolbelt_suggestion?: ToolbeltSuggestion
}

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
  onClarificationSubmit?: (values: Record<string, string | number | boolean>) => void
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Thinking stages that the AI cycles through
 */
const THINKING_STAGES = [
  { icon: 'brain', text: 'Analyzing your request...', color: 'text-brand-cornflower' },
  { icon: 'search', text: 'Searching knowledge base...', color: 'text-brand-purple' },
  { icon: 'zap', text: 'Planning actions...', color: 'text-brand-navy' },
  { icon: 'cog', text: 'Processing data...', color: 'text-brand-cornflower' },
  { icon: 'sparkles', text: 'Crafting response...', color: 'text-brand-purple' },
]

/**
 * Component to show AI thinking process with animated stages
 */
function ThinkingIndicator() {
  const [stageIndex, setStageIndex] = useState(0)
  
  useEffect(() => {
    // Cycle through thinking stages every 2 seconds
    const interval = setInterval(() => {
      setStageIndex((prev) => (prev + 1) % THINKING_STAGES.length)
    }, 2000)
    
    return () => clearInterval(interval)
  }, [])
  
  const currentStage = THINKING_STAGES[stageIndex]
  
  // Map icon names to actual icon components
  const getIcon = (iconName: string) => {
    const iconMap: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
      brain: Icons.brain,
      search: Icons.search,
      zap: Icons.zap,
      cog: Icons.settings,
      sparkles: Icons.sparkles,
    }
    const IconComponent = iconMap[iconName] || Icons.sparkles
    return <IconComponent className="h-4 w-4" strokeWidth={1.5} />
  }
  
  return (
    <div className="flex flex-col gap-3 py-2">
      {/* Animated dots */}
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          <span 
            className="h-2 w-2 animate-bounce rounded-full bg-brand-cornflower/60" 
            style={{ animationDelay: '0ms' }} 
          />
          <span 
            className="h-2 w-2 animate-bounce rounded-full bg-brand-purple/60" 
            style={{ animationDelay: '150ms' }} 
          />
          <span 
            className="h-2 w-2 animate-bounce rounded-full bg-brand-navy/60" 
            style={{ animationDelay: '300ms' }} 
          />
        </div>
      </div>
      
      {/* Current thinking stage */}
      <div className="flex items-center gap-2 animate-fade-in">
        <div className={cn('transition-colors duration-300', currentStage.color)}>
          {getIcon(currentStage.icon)}
        </div>
        <span className="text-sm text-muted-foreground font-medium">
          {currentStage.text}
        </span>
      </div>
      
      {/* Progress indicators for all stages */}
      <div className="flex gap-1.5 mt-1">
        {THINKING_STAGES.map((stage, idx) => (
          <div
            key={idx}
            className={cn(
              'h-1 rounded-full transition-all duration-500',
              idx === stageIndex 
                ? 'w-8 bg-gradient-to-r from-brand-cornflower to-brand-purple' 
                : idx < stageIndex 
                ? 'w-4 bg-brand-muted/40'
                : 'w-4 bg-brand-muted/20'
            )}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Render inline markdown (bold, code, italic) in text
 */
function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Match **bold**, `code`, and *italic*
  const regex = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*]+\*)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const segment = match[0]
    if (segment.startsWith('**') && segment.endsWith('**')) {
      // Bold
      parts.push(
        <strong key={keyIndex++} className="font-semibold text-foreground">
          {segment.slice(2, -2)}
        </strong>
      )
    } else if (segment.startsWith('`') && segment.endsWith('`')) {
      // Code
      parts.push(
        <code
          key={keyIndex++}
          className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-mono text-brand-navy"
        >
          {segment.slice(1, -1)}
        </code>
      )
    } else if (segment.startsWith('*') && segment.endsWith('*')) {
      // Italic
      parts.push(
        <em key={keyIndex++} className="italic">
          {segment.slice(1, -1)}
        </em>
      )
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Render markdown links [text](url) - returns a fragment to avoid array issues
 */
function renderMarkdownLinks(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  // Match markdown links: [text](url)
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(<React.Fragment key={`text-${keyIndex++}`}>{renderInlineMarkdown(text.slice(lastIndex, match.index))}</React.Fragment>)
    }

    const linkText = match[1]
    const linkUrl = match[2]
    
    // Check if it's a PDF download link
    const isPdfLink = linkUrl.includes('/reports/') && linkUrl.includes('/download')
    
    if (isPdfLink) {
      // Handler for programmatic PDF download with auth
      const handleDownload = async (e: React.MouseEvent) => {
        e.preventDefault()
        try {
          const { buildApiUrl } = await import('@/lib/api-url')
          const fullDownloadUrl = buildApiUrl(linkUrl)
          
          // Get the session for auth token
          const { getSession } = await import('next-auth/react')
          const session = await getSession()
          
          if (!session?.accessToken) {
            throw new Error('No authentication token available. Please log in again.')
          }
          
          // Fetch with auth headers
          const response = await fetch(fullDownloadUrl, {
            headers: {
              'Authorization': `Bearer ${session.accessToken}`,
            },
          })
          
          if (!response.ok) {
            if (response.status === 401) {
              throw new Error('Authentication expired. Please log in again.')
            } else if (response.status === 404) {
              throw new Error('Report not found. It may have been deleted.')
            } else {
              throw new Error(`Failed to download PDF: ${response.statusText}`)
            }
          }
          
          // Get the blob
          const blob = await response.blob()
          
          // Extract filename from Content-Disposition header or use default
          const contentDisposition = response.headers.get('content-disposition')
          let filename = 'report.pdf'
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
            if (filenameMatch) {
              filename = filenameMatch[1]
            }
          }
          
          // Create download link and trigger
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = filename
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        } catch (error) {
          console.error('Error downloading PDF:', error)
          const message = error instanceof Error ? error.message : 'Failed to download PDF. Please try again.'
          alert(message)
        }
      }
      
      // Render PDF download button
      parts.push(
        <button
          key={keyIndex++}
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-navy hover:bg-brand-navy/90 text-white rounded-lg transition-colors text-sm font-medium shadow-sm hover:shadow-md"
        >
          <Icons.fileText className="h-4 w-4" />
          <span>{linkText}</span>
        </button>
      )
    } else {
      // Regular link
      parts.push(
        <a
          key={keyIndex++}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-cornflower hover:text-brand-purple underline transition-colors"
        >
          {linkText}
        </a>
      )
    }
    
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(<React.Fragment key={`text-${keyIndex++}`}>{renderInlineMarkdown(text.slice(lastIndex))}</React.Fragment>)
  }

  // Return a single fragment instead of array to avoid React key warnings
  return parts.length > 0 ? <>{parts}</> : renderInlineMarkdown(text)
}

/**
 * Parse and render markdown tables
 */
function parseMarkdownTable(lines: string[], startIndex: number): { table: React.ReactNode, endIndex: number } | null {
  // Check if we have a table header and separator
  if (startIndex + 1 >= lines.length) return null
  
  const headerLine = lines[startIndex].trim()
  const separatorLine = lines[startIndex + 1].trim()
  
  // Validate table format - must have pipes
  if (!headerLine.includes('|') || !separatorLine.includes('|')) return null
  
  // Separator line should be mostly dashes and pipes (be more lenient)
  // Examples: |---|---|, | --- | --- |, |-----|-----|
  const separatorTest = separatorLine.replace(/[\|\s\-:]+/g, '')
  if (separatorTest.length > 0) return null  // Should be empty after removing valid chars
  
  // Must have at least 2 dashes in separator
  if ((separatorLine.match(/-/g) || []).length < 2) return null
  
  // Parse headers - handle both | header | and |header| formats
  const headers = headerLine
    .split('|')
    .map(h => h.trim())
    .filter(h => h.length > 0)
  
  if (headers.length === 0) return null
  
  // Parse rows
  const rows: string[][] = []
  let currentIndex = startIndex + 2
  
  while (currentIndex < lines.length) {
    const line = lines[currentIndex].trim()
    if (!line || !line.includes('|')) break
    
    // Split by pipe and clean up
    const cells = line
      .split('|')
      .map(c => c.trim())
      .filter(c => c.length > 0)  // Remove all empty cells
    
    if (cells.length > 0) {
      // Ensure we have the right number of cells (pad or truncate)
      while (cells.length < headers.length) cells.push('-')
      rows.push(cells.slice(0, headers.length))
    }
    currentIndex++
  }
  
  // Only create table if we have at least one data row
  if (rows.length === 0) return null
  
  // Render table
  const table = (
    <div className="overflow-x-auto my-3 rounded-lg border border-border/50 shadow-sm">
      <table className="min-w-full divide-y divide-border/50 text-sm">
        <thead className="bg-brand-navy/5">
          <tr>
            {headers.map((header, idx) => (
              <th
                key={idx}
                className="px-4 py-3 text-left text-xs font-semibold text-brand-navy uppercase tracking-wider"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-border/30">
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-brand-navy/5 transition-colors">
              {row.map((cell, cellIdx) => (
                <td
                  key={cellIdx}
                  className="px-4 py-3 text-sm text-foreground"
                  style={{ wordBreak: 'break-word' }}
                >
                  {renderMarkdownLinks(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
  
  return { table, endIndex: currentIndex - 1 }
}

/**
 * Render markdown content with support for headers, lists, blockquotes, links, and tables
 */
function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let i = 0
  
  while (i < lines.length) {
    const line = lines[i]
    const trimmedLine = line.trim()
    
    // Empty line - add spacing
    if (!trimmedLine) {
      elements.push(<div key={`empty-${i}`} className="h-1" />)
      i++
      continue
    }
    
    // Try to parse table - check if line has pipes and next line looks like separator
    if (trimmedLine.includes('|') && i + 1 < lines.length) {
      const nextLine = lines[i + 1].trim()
      // Check if next line is a separator (has pipes and dashes)
      if (nextLine.includes('|') && nextLine.includes('-') && nextLine.match(/-{2,}/)) {
        const tableResult = parseMarkdownTable(lines, i)
        if (tableResult) {
          elements.push(<React.Fragment key={`table-${i}`}>{tableResult.table}</React.Fragment>)
          i = tableResult.endIndex + 1
          continue
        }
      }
    }
    
    // Headers (##, ###)
    if (trimmedLine.startsWith('### ')) {
      elements.push(
        <h4 key={`h4-${i}`} className="font-semibold text-foreground mt-3 first:mt-0">
          {renderMarkdownLinks(trimmedLine.slice(4))}
        </h4>
      )
      i++
      continue
    }
    if (trimmedLine.startsWith('## ')) {
      elements.push(
        <h3 key={`h3-${i}`} className="font-bold text-foreground mt-3 first:mt-0">
          {renderMarkdownLinks(trimmedLine.slice(3))}
        </h3>
      )
      i++
      continue
    }
    
    // Blockquote
    if (trimmedLine.startsWith('> ')) {
      elements.push(
        <blockquote
          key={`quote-${i}`}
          className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic"
        >
          {renderMarkdownLinks(trimmedLine.slice(2))}
        </blockquote>
      )
      i++
      continue
    }
    
    // Bullet points (* or -)
    if (/^[*\-]\s/.test(trimmedLine)) {
      elements.push(
        <div key={`bullet-${i}`} className="flex gap-2 pl-1">
          <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/60 mt-2 flex-shrink-0" />
          <span>{renderMarkdownLinks(trimmedLine.replace(/^[*\-]\s/, ''))}</span>
        </div>
      )
      i++
      continue
    }
    
    // Numbered lists
    if (/^\d+\.\s/.test(trimmedLine)) {
      const match = trimmedLine.match(/^(\d+)\.\s(.*)/)
      if (match) {
        elements.push(
          <div key={`numbered-${i}`} className="flex gap-2 pl-1">
            <span className="text-brand-cornflower font-medium min-w-[1.25rem]">
              {match[1]}.
            </span>
            <span>{renderMarkdownLinks(match[2])}</span>
          </div>
        )
        i++
        continue
      }
    }
    
    // Regular paragraph
    elements.push(<p key={`p-${i}`}>{renderMarkdownLinks(trimmedLine)}</p>)
    i++
  }
  
  return <div className="space-y-2 text-sm leading-relaxed">{elements}</div>
}

export function ChatMessage({ message, userName, userImage, onClarificationSubmit }: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading
  
  // Check if message has UI components
  const messageWithComponents = message as ChatMessageType & { uiComponents?: UIComponentData[] }
  const hasUIComponents = messageWithComponents.uiComponents && messageWithComponents.uiComponents.length > 0
  
  // Check if message needs clarification
  const hasClarification = message.missingParams && Object.keys(message.missingParams).length > 0
  
  // Extract metadata (NEW: For integration badge and toolbelt suggestions)
  const messageWithMetadata = message as ChatMessageType & { metadata?: MessageMetadata }
  const metadata = messageWithMetadata.metadata

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex flex-col gap-1',
        hasUIComponents ? 'max-w-[95%]' : 'max-w-[85%]',
        isUser ? 'items-end' : 'items-start'
      )}>
        {/* Text Content */}
        {message.content && (
          <div
            className={cn(
              'rounded-2xl px-4 py-3',
              'transition-all duration-200',
              isUser
                ? 'bg-brand-navy text-white rounded-br-md'
                : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
              isLoading && 'animate-pulse'
            )}
          >
            {isLoading ? (
              <ThinkingIndicator />
            ) : isUser ? (
              <p className="text-sm whitespace-pre-wrap leading-relaxed">
                {message.content}
              </p>
            ) : (
              <MarkdownContent content={message.content} />
            )}
          </div>
        )}

        {/* UI Components (Charts, Tables, etc.) */}
        {hasUIComponents && (
          <div className="w-full">
            <DynamicWidgetRenderer components={messageWithComponents.uiComponents!} />
          </div>
        )}

        {/* Clarification Form */}
        {hasClarification && onClarificationSubmit && (
          <div className="w-full mt-3">
            <ClarificationForm
              params={message.missingParams!}
              message={message.content}
              onSubmit={onClarificationSubmit}
            />
          </div>
        )}

        {/* Timestamp */}
        <span className={cn(
          'text-[10px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )} suppressHydrationWarning>
          {formatTime(message.timestamp)}
        </span>

        {/* Tool calls indicator */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex items-center gap-1.5 text-[10px] text-brand-cornflower pl-1">
            <Icons.zap className="h-3 w-3" strokeWidth={1.5} />
            <span>Used {message.toolCalls.length} tool{message.toolCalls.length > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Integration Badge (NEW: Shows data source attribution) */}
        {!isUser && metadata?.integration_badge && (
          <div className="flex items-center gap-2 pl-1 mt-1">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-navy/10 text-brand-navy text-[10px] font-medium">
              <Icons.server className="h-3 w-3" />
              #{metadata.integration_badge.name.replace(/\s+/g, '_')}
            </span>
            <span className="text-[10px] text-muted-foreground/60">
              Data from {metadata.integration_badge.system_type}
            </span>
          </div>
        )}

        {/* Toolbelt Addition Suggestion (NEW: Prompts to add fallback commands) */}
        {!isUser && metadata?.toolbelt_suggestion?.should_add && (
          <div className="mt-2 pl-1">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs">
              <Icons.lightbulb className="h-3.5 w-3.5" />
              <span>This command executed successfully via fallback. Add it to your toolbelt for faster execution next time.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
